import { DataType, Form, FormOptions, Section, useNotify } from '@globaleris/ui.library'
import { Box, Button, makeStyles, useTheme } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCriticidad } from '../../../../api/oData/enums/enumCriticidad'
import { EnumEstadoTarea } from '../../../../api/oData/enums/enumEstadoTarea'
import { CardWithTransition } from '../../../../components/shared/cardWithTransition/CardWithTransition'
import { enumToSelectOptionArray } from '../../../../helpers'
import { getCustomerDomain } from '../../../../redux'
import { useGetTaskTypes } from '../../hooks/tasks.hooks'
import { mapTaskTypeResponseToSelectOptionArray } from '../../tasks.mapper'
import { searchFilterCardStyle } from './searchFilterCardTasks.styles'

interface SearchFilterCardTasksProps {
  selectedTaskState: EnumEstadoTarea
  showCard: boolean
  filters: TaskFormSearchFilter
  onSearch: (formState: TaskFormSearchFilter) => void
  onCancel: () => void
}

export interface TaskFormSearchFilter {
  taskType?: number[]
  criticality?: number[]
  startDate?: Date | null
  endDate?: Date | null
}

export const SearchFilterCardTasks: React.FC<SearchFilterCardTasksProps> = ({
  selectedTaskState,
  showCard,
  filters,
  onSearch,
  onCancel
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'tasks', 'enums'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(searchFilterCardStyle(currentTheme))
  const classes = useStyles()
  const { info, error } = useNotify()

  const [searchFormData, setSearchFormData] = useState<TaskFormSearchFilter>(filters)
  const [fetchTaskTypeTrigger, setFetchTaskTypeTrigger] = useState<boolean>(false)
  const { taskTypes, isFetching: isFetchingTaskTypes } = useGetTaskTypes((errorMsg: string) => {
    error(t(errorMsg))
  }, fetchTaskTypeTrigger)

  const selectedDateField =
    selectedTaskState === EnumEstadoTarea.Finalizada
      ? t('tasks:components.taskTableCard.table.columns.closingDate')
      : t('tasks:components.taskTableCard.table.columns.creationDate')

  // #region Handlers Starts
  const handleInputChange = (fieldName: string, value: any) => {
    setSearchFormData(prevState => ({
      ...prevState,
      [fieldName]: value
    }))
  }
  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  const searchFormOptions: FormOptions = {
    elevation: 0,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('tasks:components.searchFilterCardTasks.forms.analytics')
    }
  }

  const searchFormSections: Section[] = [
    {
      // title: noTitle,
      inputs: [
        {
          name: 'taskType',
          label: t('tasks:components.searchFilterCardTasks.forms.inputs.taskType'),
          type: DataType.MULTISELECT,
          selectOptions: mapTaskTypeResponseToSelectOptionArray(taskTypes),
          isLoading: isFetchingTaskTypes,
          onChange: value => handleInputChange('taskType', value)
        },
        {
          name: 'criticality',
          label: t('tasks:components.searchFilterCardTasks.forms.inputs.criticality'),
          type: DataType.MULTISELECT,
          selectOptions: enumToSelectOptionArray(EnumCriticidad, 'enums:EnumCriticidad'),
          onChange: value => handleInputChange('criticality', value)
        },
        {
          name: 'startDate',
          label: t('tasks:components.searchFilterCardTasks.forms.inputs.startDate'),
          type: DataType.DATE,
          clearable: true,
          onChange: value => handleInputChange('startDate', value),
          helpText: t('tasks:components.searchFilterCardTasks.forms.helptext.startDate', {
            selectedDateField: selectedDateField
          })
        },
        {
          name: 'endDate',
          label: t('tasks:components.searchFilterCardTasks.forms.inputs.endDate'),
          type: DataType.DATE,
          clearable: true,
          onChange: value => handleInputChange('endDate', value),
          helpText: t('tasks:components.searchFilterCardTasks.forms.helptext.endDate', {
            selectedDateField: selectedDateField
          })
        }
      ]
    }
  ]
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  useEffect(() => {
    if (showCard) {
      !taskTypes.length && setFetchTaskTypeTrigger(true)
    } else {
      setFetchTaskTypeTrigger(false)
    }
  }, [showCard])
  // #endregion Hooks Ends

  return (
    <CardWithTransition show={showCard} unmountOnExit={true} className={classes.card}>
      <Form
        title={t('tasks:components.searchFilterCardTasks.forms.title')}
        sections={searchFormSections}
        data={searchFormData}
        options={searchFormOptions}
      />
      <Box className={classes.footer}>
        <Button variant="text" color="primary" size="small" onClick={onCancel}>
          {t('tasks:components.searchFilterCardTasks.actions.cancel')}
        </Button>
        <Button
          variant="text"
          color="primary"
          size="small"
          onClick={() => setSearchFormData({} as TaskFormSearchFilter)}
        >
          {t('tasks:components.searchFilterCardTasks.actions.clean')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => {
            onSearch(searchFormData)
          }}
        >
          {t('tasks:components.searchFilterCardTasks.actions.search')}
        </Button>
      </Box>
    </CardWithTransition>
  )
}
